"use client";

import type { JSX } from "react";
import { useEffect, useLayoutEffect, useRef, useState } from "react";
import { motion, type Variants } from "framer-motion";
import { gsap } from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import { Card, CardContent } from "@/components/ui/card";
import { BUSINESS_SUPPORT_VIDEO_URL } from "@/lib/video-assets";

const headingVariants: Variants = {
  hidden: { opacity: 0, filter: "blur(8px)" },
  visible: {
    opacity: 1,
    filter: "blur(0px)",
    transition: { duration: 0.8, ease: [0.16, 1, 0.3, 1] },
  },
};

const orderRows = [
  {
    image: "/home-v3/amazon.png",
    value: "213",
    imageClassName: "h-[35.41px] w-[35.41px]",
  },
  {
    image: "/home-v3/shopify.png",
    value: "189",
    imageClassName: "h-[29px] w-[29px]",
  },
  {
    image: "/home-v3/third.png",
    value: "87",
    imageClassName: "h-[30px] w-[25px]",
  },
];

const supportCards = [
  {
    title: "Keep Your Records Clean",
    description:
      "Your transactions, receipts, invoices, and monthly records stay easier to manage.",
    icon: "/home-v3/component-1-5.svg",
    iconSize: "h-[27.77px] w-[27.77px]",
    action: "Track Your Finances",
    actionIcon: "/home-v3/component-1.svg",
    variant: "revenue",
    startRotate: -16,
    restRotate: -6,
  },
  {
    title: "Know Where You Stand",
    description:
      "You get clearer numbers, so you can understand what is happening inside your business",
    icon: "/home-v3/component-1-1.svg",
    iconSize: "h-[26.41px] w-[26.41px]",
    action: "Analyze Your Sales",
    actionIcon: "/home-v3/component-1.svg",
    variant: "orders",
    startRotate: 0,
    restRotate: 0,
  },
  {
    title: "Stay Ready for Deadlines",
    description:
      "We help you stay prepared before filing dates and company obligations become stressful.",
    icon: "/home-v3/component-1-1.svg",
    iconSize: "h-[26.41px] w-[26.41px]",
    variant: "deadlines",
    startRotate: 16,
    restRotate: 6,
  },
];

// A frosted-glass highlight sitting within the top of each card's variant
// panel (revenue/orders panel, or the deadlines illustration): a
// `foreignObject` div blurs whatever is actually behind it on the page
// (Figma's standard export for a shape with a "Background blur" layer
// effect), then a semi-transparent white ellipse tints the blurred result.
// This only works inlined in the DOM — an `<img src="...svg">` renders in an
// isolated context with nothing behind it to blur, so it must be JSX, not a
// file reference like Shine.svg above. `idPrefix` keeps the clip-path id
// unique per card. Positioned `top-0` on a `relative` wrapper around each
// variant's own content, rather than a fixed pixel offset from the card's
// top — the three cards' descriptions wrap to different line counts, so a
// fixed offset landed in the empty gap above the "deadlines" card's
// illustration instead of on it.
const CardCenterGlow = ({ idPrefix }: { idPrefix: string }): JSX.Element => (
  <svg
    className="pointer-events-none absolute left-1/2 top-0 h-[135px] w-[219px] -translate-x-[calc(50%-24px)]"
    viewBox="0 0 219 135"
    fill="none"
    aria-hidden="true"
  >
    <foreignObject x="-1214.55" y="-1214.55" width="2647.58" height="2563.38">
      {/* React resets the namespace to HTML for foreignObject children, so
          this renders as a real <div> without an explicit xmlns attribute
          (which the DOM typings for a div don't allow anyway). */}
      <div
        style={{
          backdropFilter: "blur(607.28px)",
          clipPath: `url(#${idPrefix}-clip)`,
          height: "100%",
          width: "100%",
        }}
      />
    </foreignObject>
    <ellipse
      data-figma-bg-blur-radius="1214.55"
      cx="109.238"
      cy="67.1359"
      rx="109.238"
      ry="67.1359"
      fill="white"
      fillOpacity="0.25"
    />
    <defs>
      <clipPath id={`${idPrefix}-clip`} transform="translate(1214.55 1214.55)">
        <ellipse cx="109.238" cy="67.1359" rx="109.238" ry="67.1359" />
      </clipPath>
    </defs>
  </svg>
);

const CARD_START_Y = 700;
// Each card owns a 1-unit slot on the scrub timeline. It stays hidden until its
// own slot begins, then fades to full opacity over this fraction of the slot —
// short enough that the card is solid for nearly all of its travel, so only the
// card currently sliding up (and the ones already stacked) are ever visible.
const CARD_FADE_IN = 0.2;

export const BusinessSupportOptionsSection = (): JSX.Element => {
  const sectionRef = useRef<HTMLElement>(null);
  const videoPinRef = useRef<HTMLDivElement>(null);
  const cardsPinRef = useRef<HTMLDivElement>(null);
  const cardRefs = useRef<(HTMLDivElement | null)[]>([]);
  const [shouldLoadVideo, setShouldLoadVideo] = useState(false);

  useEffect(() => {
    const section = sectionRef.current;
    if (!section) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (!entry.isIntersecting) return;

        setShouldLoadVideo(true);
        observer.disconnect();
      },
      {
        // Start the download shortly before the section reaches the viewport.
        rootMargin: "600px 0px",
      },
    );

    observer.observe(section);
    return () => observer.disconnect();
  }, []);

  // useLayoutEffect (not useEffect): this creates a ScrollTrigger `pin`, which
  // reparents the pinned DOM node into a GSAP-created `.pin-spacer` div,
  // outside of React's knowledge. useEffect's cleanup for an unmounting
  // component runs asynchronously, *after* React has already removed the old
  // DOM subtree from its recorded parent — but if the pin-spacer is still in
  // place at that point, the real parent no longer matches what React
  // expects, and `parent.removeChild(node)` throws "The node to be removed
  // is not a child of this node." useLayoutEffect's cleanup runs
  // synchronously as part of the same commit, before that removal, so
  // ScrollTrigger has already unwrapped the spacer by the time React touches
  // the DOM.
  useLayoutEffect(() => {
    const cards = cardRefs.current.filter(
      (el): el is HTMLDivElement => el !== null,
    );
    if (!sectionRef.current || !videoPinRef.current || !cardsPinRef.current)
      return;
    if (cards.length === 0) return;

    gsap.registerPlugin(ScrollTrigger);

let cancelled = false;

    // gsap.matchMedia scopes the whole scroll animation to desktop widths and
    // automatically reverts it (restoring the cards to their natural, static
    // layout) below the breakpoint or on resize. On phones the animation is
    // never built, so the cards simply render as a plain vertical list — they
    // are always visible because their revealed state is the default markup,
    // not something JavaScript has to switch on.
    const mm = gsap.matchMedia();

    mm.add("(min-width: 768px)", () => {
      const prefersReducedMotion = window.matchMedia(
        "(prefers-reduced-motion: reduce)",
      ).matches;

      if (prefersReducedMotion) {
        cards.forEach((el, index) => {
          gsap.set(el, { rotate: supportCards[index].restRotate });
        });
        return;
      }


      const cardsTrackDistance = window.innerHeight * 2.4;

      // The video is pinned across the *entire* section (header scroll +
      // card stacking) so it stays put as a constant backdrop the whole
      // time, while the header and cards scroll/animate normally on top of
      // it as separate sibling elements. Its pin distance is set explicitly
      // (rather than "bottom bottom") because the cards' own pin-spacer
      // below inflates the section's height only *after* it's created, so
      // measuring "bottom bottom" ends the video's pin far too early.The
      // header's own scroll-away height is measured live inside `end` (not
      // captured once outside it) so that a later `ScrollTrigger.refresh()`
      // — e.g. once the header's custom fonts finish swapping in and reflow
      // it — recalculates against the header's *current* height instead of
      // replaying a stale measurement from before the fonts loaded.

      ScrollTrigger.create({
        trigger: sectionRef.current,
        start: "top top",
        
        end: () => {
          const headerHeight =
            cardsPinRef.current!.getBoundingClientRect().top -
            sectionRef.current!.getBoundingClientRect().top;
          return `+=${headerHeight + cardsTrackDistance}`;
        },
        pin:  videoPinRef.current,
        pinSpacing: false,
      });

      const timeline = gsap.timeline({
        scrollTrigger: {
          trigger: cardsPinRef.current,
          start: "top top",
          end: () => `+=${cardsTrackDistance}`,
          scrub: 1,
          pin: true,
          invalidateOnRefresh: true,
        },
      });

      cards.forEach((el, index) => {
        const meta = supportCards[index];
        timeline.fromTo(
          el,
          {
            y: CARD_START_Y,
            rotate: meta.startRotate,
            scale: 0.92,
            opacity: 0,
          },
          {
            y: 0,
            rotate: meta.restRotate,
            scale: 1,
            duration: 1,
            ease: "none",
          },
          index,
        );

        // Separate, much shorter tween so opacity is not scrubbed across the
        // card's whole slot the way the transform is.
        timeline.to(
          el,
          { opacity: 1, duration: CARD_FADE_IN, ease: "none" },
          index,
        );
      });

      ScrollTrigger.refresh();
    });

      // The header's custom OTF fonts (font-display: swap) can still be
    // loading when the ScrollTrigger above is first built, so `headerHeight`
    // may be measured against the fallback font's metrics. Once the fonts
    // finish (bounded — and fast now that they're preloaded — unlike
    // `window` "load", which waits on every asset on the whole page),
    // refresh so the live `end` callback re-measures against the swapped-in
    // font.
    if (typeof document !== "undefined" && "fonts" in document) {
      document.fonts.ready.then(() => {
        if (!cancelled) ScrollTrigger.refresh();
      });
    }

    return () => {
      cancelled = true;
      mm.revert();
    };
  }, []);

  return (
    <section
      ref={sectionRef}
      className="relative isolate w-full bg-black text-white"
      aria-labelledby="business-support-heading"
    >
      <div
        ref={videoPinRef}
        className="absolute inset-x-0 top-0 -z-10 h-screen w-full overflow-hidden"
      >
        <video
          className="pointer-events-none h-full w-full object-cover"
          autoPlay
          loop
          muted
          playsInline
          preload="none"
          aria-hidden="true"
        >
          {shouldLoadVideo ? (
            <source src={BUSINESS_SUPPORT_VIDEO_URL} type="video/mp4" />
          ) : null}
        </video>
        <div className="pointer-events-none absolute inset-0 bg-black/60" />
      </div>

      <header className="relative mx-auto flex max-w-[937px] flex-col items-center px-4 sm:pb-16 pt-24 text-center">
        <p className="animate-revolving-border rounded-[30px] bg-[#6a6a6a80] px-4 py-2 [font-family:'Suisse_Intl_Trial-Medium',Helvetica] text-sm font-medium leading-[23.4px] text-white backdrop-blur-[3.5px]">
          Support for every business type
        </p>
        <motion.h2
          id="business-support-heading"
          className="mt-6 [font-family:'Suisse_Works_Trial-Regular',Helvetica] text-4xl font-normal leading-[1.2] tracking-[-1.92px] text-white [text-shadow:0px_4px_24px_#0000000d] sm:text-5xl sm:leading-[64px]"
          variants={headingVariants}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, amount: 0.6 }}
        >
                  Whether you sell, serve, build, trade, or operate online, we help keep
          the financial side of your business easier to manage.
        </motion.h2>
      </header>

      <div
        ref={cardsPinRef}
        className="relative mt-12 w-full md:mt-[100px] md:h-screen md:overflow-hidden"
      >
        <div className="flex w-full items-start justify-center px-4 pb-16 md:h-full md:pb-0 md:pt-20 2xl:items-center 2xl:pt-0">
          {/* The 2xl/3xl heights use min() so the card grows on large screens
              but never exceeds the pinned h-screen container, whose
              overflow-hidden would otherwise crop it on short viewports. The
              same expression is repeated on CardContent below — the Card is
              absolutely positioned to this wrapper, so the two must agree. */}
          <div className="relative flex w-full max-w-[460px] flex-col gap-6 md:block md:h-[626px] md:gap-0 2xl:h-[min(720px,calc(100vh_-_160px))] 2xl:max-w-[540px] 3xl:h-[min(790px,calc(100vh_-_180px))] 3xl:max-w-[600px]">
            {supportCards.map((card, index) => (
              <Card
                key={card.title}
                ref={(el) => {
                  cardRefs.current[index] = el;
                }}
                style={{ zIndex: 10 + index }}
                className="relative w-full overflow-hidden rounded-[37.02px] border-0 bg-[#0F1319] text-white shadow-2xl md:absolute md:inset-0 [will-change:transform,opacity]"
              >
                <img
                  className="pointer-events-none absolute  left-1/2 top-0 h-auto w-full -translate-x-1/2"
                  alt=""
                  aria-hidden="true"
                  src="/home-v3/Shine.svg"
                />
                <CardContent className="relative flex h-[626px] flex-col items-start gap-[18.51px] p-[37.02px] 2xl:h-[min(720px,calc(100vh_-_160px))] 2xl:gap-[22px] 2xl:p-[44px] 3xl:h-[min(790px,calc(100vh_-_180px))] 3xl:gap-[24px] 3xl:p-[48px]">
                  <div className="inline-flex rounded-[63.63px] border-[1.16px] border-[#0b3bff] p-[23.14px] 2xl:p-[27px] 3xl:p-[30px]">
                    <img
                      className={card.iconSize}
                      alt="Component"
                      src={card.icon}
                    />
                  </div>
                  <div className="pt-[3.93px]">
                    <h3 className="font-inter text-[27.8px] font-bold leading-[37px] text-white 2xl:text-[32px] 2xl:leading-[42px] 3xl:text-[35px] 3xl:leading-[46px]">
                      {card.title}
                    </h3>
                  </div>
                  <p className="font-inter text-[18.5px] font-normal leading-[27.8px] text-white 2xl:text-[21px] 2xl:leading-[31px] 3xl:text-[23px] 3xl:leading-[34px]">
                    {card.description}
                  </p>
                  <div className="relative w-full">
                    {/* <CardCenterGlow idPrefix={`card-center-glow-${index}`} /> */}

                    {card.variant === "revenue" && (
                      <div className="flex h-[203.62px] w-full flex-col gap-[9.26px] overflow-hidden rounded-[27.77px] border-[1.16px] border-[#e5e7eb4f] bg-[#ffffff33] px-[32.39px] pb-[23.14px] pt-[32.39px] backdrop-blur-[20px] 2xl:h-[235px] 2xl:px-[38px] 2xl:pt-[38px] 3xl:h-[255px]">
                        <div className="flex flex-col gap-[4.63px]">
                          <p className="font-inter text-[20.8px] font-medium leading-normal tracking-[-0.1px] text-white 2xl:text-[23px] 3xl:text-[25px]">
                            Total Revenue
                          </p>
                          <p className="font-inter text-[39.3px] font-bold leading-normal tracking-[-0.39px] text-white 2xl:text-[44px] 3xl:text-[48px]">
                            $112,780
                          </p>
                        </div>
                        <img
                          className="min-h-0 w-[75%] h-[80px] flex-1 object-cover 2xl:h-[95px]"
                          alt="Chart container"
                          src="/home-v3/graph.svg"
                        />
                      </div>
                    )}

                    {card.variant === "orders" && (
                      <div className="flex w-full flex-col gap-[17.67px] rounded-[20.63px] border-[0.86px] border-[#e5e7eb4f] bg-[#ffffff33] p-[27.51px] backdrop-blur-[20px] 2xl:gap-[21px] 2xl:p-[32px] 3xl:gap-[23px] 3xl:p-[35px]">
                        {orderRows.map((row) => (
                          <div
                            key={row.value}
                            className="flex items-center justify-between"
                          >
                            <div className="flex items-center gap-[13.75px]">
                              <div className="flex h-[35.41px] w-[35.41px] items-center justify-center">
                                <img
                                  className={`${row.imageClassName} object-cover`}
                                  alt="Image"
                                  src={row.image}
                                />
                              </div>
                              <span className="font-inter text-[13.8px] font-medium leading-normal text-white 2xl:text-[15.5px] 3xl:text-[17px]">
                                Total Orders
                              </span>
                            </div>
                            <span className="font-inter text-[15.5px] font-normal leading-normal text-[#ff4e4e] 2xl:text-[17.5px] 3xl:text-[19px]">
                              {row.value}
                            </span>
                          </div>
                        ))}
                      </div>
                    )}

                    {card.variant === "deadlines" && (
                      // `home.svg` already bakes in its own frosted-glass treatment
                      // internally (a 20%-opacity white rect plus its own
                      // background-blur, the same Figma export pattern as
                      // CardCenterGlow) — wrapping it in another bg-white/backdrop-blur
                      // div, as tried previously, stacked a second translucent white
                      // layer on top and washed out its color. `isolate` fixes the
                      // actual problem (paint order) without adding any visual layer:
                      // `isolation: isolate` creates a stacking context with zero
                      // rendering side effects, which is all that's needed to make
                      // this <img> paint over CardCenterGlow the same way backdrop-blur
                      // does for the revenue/orders panels above.
                        <img
                        className="isolate w-full"
                        alt="Home"
                        src="/home-v3/home.svg"
                      />
                    )}
                  </div>

                  {card.action && card.actionIcon && (
                    <button
                      className="mt-auto inline-flex h-auto items-center gap-[9.26px] pt-[18.51px] text-left font-inter text-[18.5px] font-normal leading-[27.8px] text-[#0061fe] 2xl:text-[21px] 3xl:text-[23px]"
                      type="button"
                    >
                      <span>{card.action}</span>
                      <img
                        className="h-[18.51px] w-[18.51px] 2xl:h-[21px] 2xl:w-[21px] 3xl:h-[23px] 3xl:w-[23px]"
                        alt="Component"
                        src={card.actionIcon}
                      />
                    </button>
                  )}
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};
